import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { logo } from "@/lib/site";
import { useLang } from "@/lib/i18n";
import { legal } from "@/lib/legal";
import LanguageSwitcher from "@/components/LanguageSwitcher";

export default function LegalPage({ type }) {
  const { t, lang } = useLang();
  const doc = legal[type][lang] || legal[type].en;

  useEffect(() => {
    const lenis = window.__lenis;
    if (lenis) lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [type]);

  return (
    <div className="noise relative min-h-screen bg-background text-foreground" data-testid={`legal-page-${type}`}>
      <div className="pointer-events-none absolute -top-40 -right-32 h-[32rem] w-[32rem] rounded-full bg-primary/15 blur-[150px]" />

      {/* top bar */}
      <header className="relative z-10 mx-auto w-full max-w-4xl px-6 md:px-10 h-24 flex items-center justify-between">
        <Link to="/" data-testid="legal-logo-home" className="flex items-center">
          <img src={logo} alt="LocalNova Web Design" className="h-12 md:h-14 w-auto drop-shadow-[0_0_20px_hsl(180_80%_45%/0.4)]" />
        </Link>
        <LanguageSwitcher testid={`lang-switcher-${type}`} />
      </header>

      <main className="relative z-10 mx-auto w-full max-w-3xl px-6 md:px-10 pt-8 pb-24">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        >
          <Link
            to="/"
            data-testid="legal-back-button"
            className="group inline-flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-primary transition-colors mb-8"
          >
            <ArrowLeft className="h-4 w-4 transition-transform duration-300 group-hover:-translate-x-1" />
            {t.notfound.button}
          </Link>

          <h1 className="font-heading font-bold tracking-tight text-4xl sm:text-5xl">{doc.title}</h1>
          <p className="mt-3 font-mono-x text-xs uppercase tracking-[0.2em] text-muted-foreground">
            {doc.updatedLabel}: {doc.updated}
          </p>
          <p className="mt-6 text-base text-muted-foreground leading-relaxed">{doc.intro}</p>

          <div className="mt-10 space-y-8">
            {doc.sections.map((s, i) => (
              <section key={i} data-testid={`legal-section-${i}`}>
                <h2 className="font-heading text-xl font-bold text-foreground">{s.h}</h2>
                <p className="mt-2 text-muted-foreground leading-relaxed">{s.p}</p>
              </section>
            ))}
          </div>

          <p className="mt-12 rounded-xl border border-border bg-secondary/30 px-5 py-4 text-sm text-muted-foreground italic">
            {doc.disclaimer}
          </p>
        </motion.div>
      </main>
    </div>
  );
}
